import type { GridInsert } from '@urbanmetrix/sdk';

type Coordinate = {
  x?: number | string | null;
  y?: number | string | null;
  z?: number | string | null;
};

type RawCoordinateReferenceSystem = {
  point?: Coordinate | null;
  rotation?: number | string | null;
};

export interface RawGridParams {
  legalBorder?: Coordinate[][] | Coordinate[] | null;
  legalBorders?: Coordinate[][] | null;
  buildableBorder?: Coordinate[][] | Coordinate[] | null;
  buildableBorders?: Coordinate[][] | null;
  analysisArea?: Coordinate[] | null;
  analysisAreas?: Coordinate[][] | null;
  manualCenter?: Coordinate | null;
  gridResolution?: number | string | null;
  gridAngle?: number | string | null;
  gridHeight?: number | string | null;
  customGridElevationMode?: string | null;
  customGridElevation?: number | string | null;
  terrainType?: string;
  terrainResolutionClose?: number | string | null;
  flatTerrainMode?: string | null;
  flatTerrainHeight?: number | string | null;
  isFlatTop?: boolean;
  coordinateReferenceSystem?: RawCoordinateReferenceSystem | null;
  fileForUploadedScene?: unknown;
  fileForTrees?: unknown;
}

const toNullableNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toNumber = (value: unknown, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const areSamePoint = (firstPoint: Coordinate, secondPoint: Coordinate) =>
  Number(firstPoint?.x) === Number(secondPoint?.x) && Number(firstPoint?.y) === Number(secondPoint?.y);

type GridPayloadParams = NonNullable<GridInsert['params']> & {
  customGridElevationMode: string | null;
};

const normalizePolygonPoint = (coordinate?: Coordinate | null) => {
  const nextPoint: { x: number; y: number; z?: number } = {
    x: toNumber(coordinate?.x),
    y: toNumber(coordinate?.y),
  };

  const parsedZ = toNullableNumber(coordinate?.z);
  if (parsedZ != null) {
    nextPoint.z = parsedZ;
  }

  return nextPoint;
};

const normalizeCoordinateReferencePoint = (coordinate?: Coordinate | null) => ({
  x: toNumber(coordinate?.x),
  y: toNumber(coordinate?.y),
  z: toNumber(coordinate?.z),
});

const closePolygon = (coordinates: Coordinate[] | null | undefined) => {
  if (!Array.isArray(coordinates) || coordinates.length === 0) {
    return [];
  }

  const polygon = coordinates.map(normalizePolygonPoint);
  const firstPoint = polygon[0];
  const lastPoint = polygon[polygon.length - 1];

  return areSamePoint(firstPoint, lastPoint) ? polygon : [...polygon, { ...firstPoint }];
};

const toMultiPolygonBorder = (coordinates: Coordinate[][] | Coordinate[] | null | undefined, volumeCount = 0) => {
  const polygons =
    !Array.isArray(coordinates) || coordinates.length === 0
      ? []
      : Array.isArray(coordinates[0])
        ? (coordinates as Coordinate[][])
        : [coordinates as Coordinate[]];
  return Array.from({ length: Math.max(polygons.length, volumeCount) }, (_, index) => closePolygon(polygons[index]));
};

const buildCoordinateReferenceSystem = (coordinateReferenceSystem: RawCoordinateReferenceSystem | null | undefined) => {
  return {
    point: normalizeCoordinateReferencePoint(coordinateReferenceSystem?.point),
    rotation: toNumber(coordinateReferenceSystem?.rotation),
  };
};

const hasManualCenter = (manualCenter?: Coordinate | null) =>
  Boolean(manualCenter) && (toNumber(manualCenter?.x) !== 0 || toNumber(manualCenter?.y) !== 0);

export const buildGridParamsPayload = (gridParams: RawGridParams): GridPayloadParams => {
  const analysisArea = toMultiPolygonBorder(
    gridParams.analysisAreas?.length ? gridParams.analysisAreas : gridParams.analysisArea
  );
  return {
    legalBorder: toMultiPolygonBorder(
      gridParams.legalBorders?.length ? gridParams.legalBorders : gridParams.legalBorder,
      analysisArea.length
    ),
    buildableBorder: toMultiPolygonBorder(
      gridParams.buildableBorders?.length ? gridParams.buildableBorders : gridParams.buildableBorder,
      analysisArea.length
    ),
    analysisArea,
    manualCenter: hasManualCenter(gridParams.manualCenter)
      ? {
          x: toNumber(gridParams.manualCenter?.x),
          y: toNumber(gridParams.manualCenter?.y),
        }
      : null,
    gridResolution: toNumber(gridParams.gridResolution, 1),
    gridAngle: toNumber(gridParams.gridAngle),
    gridHeight: toNumber(gridParams.gridHeight, 9),
    customGridElevationMode: gridParams.customGridElevationMode ?? null,
    customGridElevation:
      gridParams.customGridElevationMode === 'Absolute' ? toNullableNumber(gridParams.customGridElevation) : null,
    terrainType: gridParams.terrainType as GridPayloadParams['terrainType'],
    terrainResolutionClose: toNumber(gridParams.terrainResolutionClose, 0.5),
    flatTerrainMode: (gridParams.flatTerrainMode ?? null) as GridPayloadParams['flatTerrainMode'],
    flatTerrainHeight:
      toNumber(gridParams.flatTerrainHeight) > 0 ? toNullableNumber(gridParams.flatTerrainHeight) : null,
    isFlatTop: Boolean(gridParams.isFlatTop),
    coordinateReferenceSystem: buildCoordinateReferenceSystem(gridParams.coordinateReferenceSystem),
  } as unknown as GridPayloadParams;
};
